import {
  IComparatorFn,
  IEntityTypePayload,
  IErrorPayload,
} from '../entity-data/entity-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import { DocumentHistoryFilterTypesEnum } from './client-data.data';
import { List } from 'immutable';
import { ISearchParams } from '../entity-data/entity-data.data';
import { Document } from '../document-data/document.model';
import { IDispatchId } from '../../utils/helpers/redux.helper';

// action payload interfaces
export interface IClientIdPayload extends IEntityTypePayload {
  clientId: string;
}

export interface ICreateClientBankDetailsPayload
  extends IEntityTypePayload,
    IClientIdPayload,
    IDispatchId {}

export interface IDocumentFilterTypePayload extends IEntityTypePayload {
  filter: DocumentHistoryFilterTypesEnum;
}

export interface IFilterClientDocumentsPayload
  extends IClientIdPayload,
    IDocumentFilterTypePayload {}

export interface ISetClientDocuments extends IEntityTypePayload, IComparatorFn {
  documents: List<Document>;
  searchParams: ISearchParams;
}

export interface IFetchClientDocumentsError
  extends IEntityTypePayload,
    IErrorPayload {}

export interface IDocumentPayload extends IEntityTypePayload {
  document: Document;
}

export interface ICreateClientBankDetailsError
  extends IEntityTypePayload,
    IErrorPayload {}

export interface IEmailPayload {
  clientId: string;
  email: string;
}

export interface IShowJobDialogPayload extends IEntityTypePayload {
  flag: boolean;
}

// action enums
export enum ActionsEnum {
  INVOICE_CLIENT = 'INVOICE_CLIENT',
  FETCH_CLIENT_DOCUMENTS = 'FETCH_CLIENT_DOCUMENTS',
  FETCH_CLIENT_DOCUMENTS_ERROR = 'FETCH_CLIENT_DOCUMENTS_ERROR',
  FETCH_NEXT_CLIENT_DOCUMENTS_PAGE = 'FETCH_NEXT_CLIENT_DOCUMENTS_PAGE',
  CREATE_CLIENT_BANK_DETAILS = 'CREATE_CLIENT_BANK_DETAILS',
  CREATE_CLIENT_BANK_DETAILS_ERROR = 'CREATE_CLIENT_BANK_DETAILS_ERROR',
  FILTER_CLIENT_DOCUMENTS = 'FILTER_CLIENT_DOCUMENTS',
  SAVE_EDITING_CLIENT = 'SAVE_EDITING_CLIENT',
  SET_CLIENT_DOCUMENTS = 'SET_CLIENT_DOCUMENTS',
  SEND_BANK_DETAILS_TO_EMAIL = 'SEND_BANK_DETAILS_TO_EMAIL',
  SEND_BANK_DETAILS_TO_EMAIL_SUCCESS = 'SEND_BANK_DETAILS_TO_EMAIL_SUCCESS',
  SEND_BANK_DETAILS_TO_EMAIL_ERROR = 'SEND_BANK_DETAILS_TO_EMAIL_ERROR',
  CLEAR_CLIENT_DOCUMENTS = 'CLEAR_DOCUMENTS',
  UPDATE_DOCUMENT_IN_CLIENT_DOCUMENTS = 'UPDATE_DOCUMENT_IN_CLIENT_DOCUMENTS',
  DELETE_DOCUMENT_IN_CLIENT_DOCUMENTS = 'DELETE_DOCUMENT_IN_CLIENT_DOCUMENTS',
  SHOW_JOB_DIALOG = 'SHOW_JOB_DIALOG',
}

export interface InvoiceClient {
  type: ActionsEnum.INVOICE_CLIENT;
  payload: IClientIdPayload;
}

export interface FetchClientDocuments {
  type: ActionsEnum.FETCH_CLIENT_DOCUMENTS;
  payload: IFilterClientDocumentsPayload;
}

export interface FetchClientDocumentsError {
  type: ActionsEnum.FETCH_CLIENT_DOCUMENTS_ERROR;
  payload: IFetchClientDocumentsError;
}

export interface FetchNextClientDocumentsPage {
  type: ActionsEnum.FETCH_NEXT_CLIENT_DOCUMENTS_PAGE;
  payload: IClientIdPayload;
}

export interface CreateClientBankDetails {
  type: ActionsEnum.CREATE_CLIENT_BANK_DETAILS;
  payload: ICreateClientBankDetailsPayload;
}

export interface CreateClientBankDetailsError {
  type: ActionsEnum.CREATE_CLIENT_BANK_DETAILS_ERROR;
  payload: ICreateClientBankDetailsError;
}

export interface FilterClientDocuments {
  type: ActionsEnum.FILTER_CLIENT_DOCUMENTS;
  payload: IFilterClientDocumentsPayload;
}

export interface SaveEditingClient {
  type: ActionsEnum.SAVE_EDITING_CLIENT;
  payload: IDispatchId;
}

export interface SetClientDocuments {
  type: ActionsEnum.SET_CLIENT_DOCUMENTS;
  payload: ISetClientDocuments;
}

export interface ClearClientDocuments {
  type: ActionsEnum.CLEAR_CLIENT_DOCUMENTS;
  payload: IEntityTypePayload;
}

export interface UpdateDocumentInClientDocuments {
  type: ActionsEnum.UPDATE_DOCUMENT_IN_CLIENT_DOCUMENTS;
  payload: IDocumentPayload;
}

export interface DeleteDocumentInClientDocuments {
  type: ActionsEnum.DELETE_DOCUMENT_IN_CLIENT_DOCUMENTS;
  payload: IDocumentPayload;
}

export interface SendBankDetailsToEmail {
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL;
  payload: IEmailPayload;
}

export interface SendBankDetailsToEmailSuccess {
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL_SUCCESS;
  payload: IEmailPayload;
}

export interface SendBankDetailsToEmailError {
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL_ERROR;
  payload: IErrorPayload;
}

export interface ShowJobDialog {
  type: ActionsEnum.SHOW_JOB_DIALOG;
  payload: IShowJobDialogPayload;
}

// action definitions
export const invoiceClient = (
  entityType: EntityTypesEnum,
  clientId: string
): InvoiceClient => ({
  type: ActionsEnum.INVOICE_CLIENT,
  payload: {
    entityType,
    clientId,
  },
});

export const fetchClientDocuments = (
  entityType: EntityTypesEnum,
  clientId: string,
  filter: DocumentHistoryFilterTypesEnum
): FetchClientDocuments => ({
  type: ActionsEnum.FETCH_CLIENT_DOCUMENTS,
  payload: {
    entityType,
    clientId,
    filter,
  },
});

export const fetchClientDocumentsError = (
  entityType: EntityTypesEnum,
  error: Error,
  data: any[]
): FetchClientDocumentsError => ({
  type: ActionsEnum.FETCH_CLIENT_DOCUMENTS_ERROR,
  payload: {
    entityType,
    error,
    data,
  },
});

export const fetchNextClientDocumentsPage = (
  entityType: EntityTypesEnum,
  clientId: string
): FetchNextClientDocumentsPage => ({
  type: ActionsEnum.FETCH_NEXT_CLIENT_DOCUMENTS_PAGE,
  payload: {
    entityType,
    clientId,
  },
});

export const createClientBankDetails = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  clientId: string
): CreateClientBankDetails => ({
  type: ActionsEnum.CREATE_CLIENT_BANK_DETAILS,
  payload: {
    dispatchId,
    entityType,
    clientId,
  },
});

export const createClientBankDetailsError = (
  entityType: EntityTypesEnum,
  error: Error,
  data: any[]
): CreateClientBankDetailsError => ({
  type: ActionsEnum.CREATE_CLIENT_BANK_DETAILS_ERROR,
  payload: {
    entityType,
    error,
    data,
  },
});

export const filterClientDocuments = (
  entityType: EntityTypesEnum,
  clientId: string,
  filter: DocumentHistoryFilterTypesEnum
): FilterClientDocuments => ({
  type: ActionsEnum.FILTER_CLIENT_DOCUMENTS,
  payload: {
    entityType,
    clientId,
    filter,
  },
});

export const saveEditingClient = (dispatchId: string): SaveEditingClient => ({
  type: ActionsEnum.SAVE_EDITING_CLIENT,
  payload: {
    dispatchId,
  },
});

export const setClientDocuments = (
  entityType: EntityTypesEnum,
  documents: List<Document>,
  searchParams: ISearchParams,
  comparatorFn: (entityA: any, entityB: any) => number
): SetClientDocuments => ({
  type: ActionsEnum.SET_CLIENT_DOCUMENTS,
  payload: {
    entityType,
    documents,
    searchParams,
    comparatorFn,
  },
});

export const clearClientDocuments = (
  entityType: EntityTypesEnum
): ClearClientDocuments => ({
  type: ActionsEnum.CLEAR_CLIENT_DOCUMENTS,
  payload: {
    entityType,
  },
});

export const updateDocumentInClientDocuments = (
  entityType: EntityTypesEnum,
  document: Document
): UpdateDocumentInClientDocuments => ({
  type: ActionsEnum.UPDATE_DOCUMENT_IN_CLIENT_DOCUMENTS,
  payload: {
    entityType,
    document,
  },
});

export const deleteDocumentInClientDocuments = (
  entityType: EntityTypesEnum,
  document: Document
): DeleteDocumentInClientDocuments => ({
  type: ActionsEnum.DELETE_DOCUMENT_IN_CLIENT_DOCUMENTS,
  payload: {
    entityType,
    document,
  },
});

export const sendBankDetailsToEmail = (
  clientId: string,
  email: string
): SendBankDetailsToEmail => ({
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL,
  payload: {
    clientId,
    email,
  },
});

export const sendBankDetailsToEmailSuccess = (
  clientId: string,
  email: string
): SendBankDetailsToEmailSuccess => ({
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL_SUCCESS,
  payload: {
    clientId,
    email,
  },
});

export const sendBankDetailsToEmailError = (
  error: Error,
  data: any[]
): SendBankDetailsToEmailError => ({
  type: ActionsEnum.SEND_BANK_DETAILS_TO_EMAIL_ERROR,
  payload: {
    error,
    data,
  },
});

export const showJobDialog = (
  entityType: EntityTypesEnum,
  flag: boolean
): ShowJobDialog => ({
  type: ActionsEnum.SHOW_JOB_DIALOG,
  payload: {
    entityType, // NOTE: need to specify entityType to allow client reducer process the action
    flag,
  },
});
